import React, { useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API_URL =
  "https://wwwp.isepsantafe.net.ar/logistica/ausencias-sin-pase";

// ============================================================
// FECHA ARGENTINA
// ============================================================
const obtenerFechaHoyArgentina = () => {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Argentina/Buenos_Aires",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch (error) {
    return new Date().toISOString().split("T")[0];
  }
};

// ============================================================
// TEXTO CAPITALIZADO
// ============================================================
const capitalizar = (texto = "") => {
  if (!texto) return "";

  return (
    texto.charAt(0).toUpperCase() +
    texto.slice(1).toLowerCase()
  );
};

// ============================================================
// SI / NO
// ============================================================
const siNo = (valor) => (valor ? "SI" : "NO");

// ============================================================
// TARJETA
// ============================================================
const Tarjeta = ({
  titulo,
  valor,
  subtitulo,
  destacado = false,
  peligro = false,
}) => {
  let background = "#ffffff";
  let border = "#dee2e6";

  if (destacado) {
    background = "#e7f5ff";
    border = "#339af0";
  }

  if (peligro) {
    background = "#fff5f5";
    border = "#fa5252";
  }

  return (
    <div
      style={{
        background,
        border: `1px solid ${border}`,
        borderRadius: "12px",
        padding: "16px",
        minWidth: "180px",
        flex: "1 1 180px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          fontSize: "13px",
          color: "#6c757d",
          marginBottom: "5px",
          fontWeight: "600",
        }}
      >
        {titulo}
      </div>

      <div
        style={{
          fontSize: "28px",
          fontWeight: "700",
          color: "#212529",
        }}
      >
        {valor ?? 0}
      </div>

      {subtitulo && (
        <div
          style={{
            marginTop: "4px",
            fontSize: "12px",
            color: "#868e96",
          }}
        >
          {subtitulo}
        </div>
      )}
    </div>
  );
};

// ============================================================
// DATO
// ============================================================
const Dato = ({ label, valor, destacado = false }) => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "8px 0",
        borderBottom: "1px solid #eee",
        fontWeight: destacado ? "700" : "400",
        color: destacado ? "#1971c2" : "#343a40",
      }}
    >
      <span>{label}</span>
      <span>{valor ?? 0}</span>
    </div>
  );
};

// ============================================================
// DETALLE POR AÑO
// ============================================================
const DetallePorAnio = ({ porAnio }) => {
  if (!porAnio) return null;

  const primer = porAnio.primerAnio || {};
  const segundo = porAnio.segundoAnio || {};

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns:
          "repeat(auto-fit, minmax(280px, 1fr))",
        gap: "15px",
        marginTop: "20px",
      }}
    >
      <div style={boxStyle}>
        <h4 style={{ marginTop: 0 }}>1° Año</h4>

        <Dato
          label="Total cadetes"
          valor={primer.totalCadetes}
        />

        <Dato
          label="Asistencias"
          valor={primer.asistencias}
        />

        <Dato
          label="Presentes"
          valor={primer.presentes}
        />

        <Dato
          label="Ausentes"
          valor={primer.ausentes}
        />

        <Dato
          label="Con pase de salida"
          valor={primer.conPaseSalida}
        />

        <Dato
          label="Pases con asistencia"
          valor={primer.pasesConAsistencia}
        />

        <Dato
          label="Pases sin asistencia"
          valor={primer.pasesSinAsistencia}
        />

        <Dato
          label="Deben racionar"
          valor={primer.deberianRacionar}
          destacado
        />
      </div>

      <div style={boxStyle}>
        <h4 style={{ marginTop: 0 }}>2° Año</h4>

        <Dato
          label="Total cadetes"
          valor={segundo.totalCadetes}
        />

        <Dato
          label="Asistencias"
          valor={segundo.asistencias}
        />

        <Dato
          label="Presentes"
          valor={segundo.presentes}
        />

        <Dato
          label="Ausentes"
          valor={segundo.ausentes}
        />

        <Dato
          label="Con pase de salida"
          valor={segundo.conPaseSalida}
        />

        <Dato
          label="Pases con asistencia"
          valor={segundo.pasesConAsistencia}
        />

        <Dato
          label="Pases sin asistencia"
          valor={segundo.pasesSinAsistencia}
        />

        <Dato
          label="Deben racionar"
          valor={segundo.deberianRacionar}
          destacado
        />
      </div>
    </div>
  );
};

// ============================================================
// LISTADO CADETES
// ============================================================
const ListadoCadetes = ({
  titulo,
  listado = [],
}) => {
  if (!listado?.length) {
    return (
      <div style={{ marginBottom: "20px" }}>
        <h4>{titulo}</h4>

        <div style={emptyStyle}>
          No hay cadetes para mostrar.
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: "25px" }}>
      <h4>
        {titulo} ({listado.length})
      </h4>

      <div
        style={{
          overflowX: "auto",
          border: "1px solid #dee2e6",
          borderRadius: "10px",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr
              style={{
                background: "#f1f3f5",
              }}
            >
              <th style={thStyle}>#</th>
              <th style={thStyle}>DNI</th>
              <th style={thStyle}>Apellido</th>
              <th style={thStyle}>Nombre</th>
            </tr>
          </thead>

          <tbody>
            {listado.map((cadete, index) => (
              <tr
                key={`${cadete.dni}-${index}`}
              >
                <td style={tdStyle}>
                  {index + 1}
                </td>

                <td style={tdStyle}>
                  <strong>
                    {cadete.dni}
                  </strong>
                </td>

                <td style={tdStyle}>
                  {(
                    cadete.apellido || ""
                  ).trim()}
                </td>

                <td style={tdStyle}>
                  {(
                    cadete.nombre || ""
                  ).trim()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================
// DETALLE SEDE
// ============================================================
const DetalleSede = ({
  nombre,
  datos,
}) => {
  const [
    mostrarListado,
    setMostrarListado,
  ] = useState(false);

  const [
    mostrarPases,
    setMostrarPases,
  ] = useState(false);

  if (!datos) return null;

  const listadoPrimerAnio =
    datos?.deberianRacionarListado
      ?.primerAnio || [];

  const listadoSegundoAnio =
    datos?.deberianRacionarListado
      ?.segundoAnio || [];

  const pases =
    datos?.pasesDetalle || [];

  return (
    <div
      style={{
        marginTop: "30px",
        background: "#ffffff",
        borderRadius: "15px",
        border: "1px solid #dee2e6",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "18px 22px",
          background: "#212529",
          color: "white",
        }}
      >
        <h2 style={{ margin: 0 }}>
          Sede {nombre}
        </h2>
      </div>

      <div style={{ padding: "20px" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <Tarjeta
            titulo="Total cadetes"
            valor={datos.totalCadetes}
          />

          <Tarjeta
            titulo="Asistencias"
            valor={datos.totalAsistencias}
          />

          <Tarjeta
            titulo="Presentes"
            valor={datos.totalPresentes}
          />

          <Tarjeta
            titulo="Ausencias"
            valor={datos.totalAusencias}
            peligro={
              datos.totalAusencias > 0
            }
          />

          <Tarjeta
            titulo="Con pase"
            valor={datos.totalConPaseSalida}
          />

          <Tarjeta
            titulo="Deben racionar"
            valor={datos.deberianRacionar}
            destacado
          />
        </div>

        <DetallePorAnio
          porAnio={datos.porAnio}
        />

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            marginTop: "20px",
          }}
        >
          <button
            onClick={() =>
              setMostrarListado(
                !mostrarListado
              )
            }
            style={buttonPrimary}
          >
            {mostrarListado
              ? "Ocultar listado"
              : "Ver cadetes que deben racionar"}
          </button>

          <button
            onClick={() =>
              setMostrarPases(
                !mostrarPases
              )
            }
            style={buttonSecondary}
          >
            {mostrarPases
              ? "Ocultar pases"
              : `Ver pases (${pases.length})`}
          </button>
        </div>

        {mostrarListado && (
          <div
            style={{
              marginTop: "25px",
              borderTop:
                "1px solid #dee2e6",
              paddingTop: "20px",
            }}
          >
            <ListadoCadetes
              titulo="1° Año que deben racionar"
              listado={
                listadoPrimerAnio
              }
            />

            <ListadoCadetes
              titulo="2° Año que deben racionar"
              listado={
                listadoSegundoAnio
              }
            />
          </div>
        )}

        {mostrarPases && (
          <div
            style={{
              marginTop: "25px",
              overflowX: "auto",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr
                  style={{
                    background:
                      "#f1f3f5",
                  }}
                >
                  <th style={thStyle}>
                    DNI
                  </th>

                  <th style={thStyle}>
                    Cadete
                  </th>

                  <th style={thStyle}>
                    Año
                  </th>

                  <th style={thStyle}>
                    Asistencia
                  </th>

                  <th style={thStyle}>
                    Ausencia
                  </th>

                  <th style={thStyle}>
                    Pase
                  </th>

                  <th style={thStyle}>
                    Raciona
                  </th>

                  <th style={thStyle}>
                    Motivo
                  </th>
                </tr>
              </thead>

              <tbody>
                {pases.map(
                  (item, index) => (
                    <tr
                      key={`${item.dni}-${index}`}
                    >
                      <td style={tdStyle}>
                        {item.dni}
                      </td>

                      <td style={tdStyle}>
                        {(
                          item.apellido ||
                          ""
                        ).trim()}
                        ,{" "}
                        {(
                          item.nombre ||
                          ""
                        ).trim()}
                      </td>

                      <td style={tdStyle}>
                        {item.anio}
                      </td>

                      <td style={tdStyle}>
                        {siNo(
                          item.tieneAsistenciaEnRacionamiento
                        )}
                      </td>

                      <td style={tdStyle}>
                        {siNo(
                          item.tieneAusencia
                        )}
                      </td>

                      <td style={tdStyle}>
                        {siNo(
                          item.tienePaseSalida
                        )}
                      </td>

                      <td style={tdStyle}>
                        {siNo(
                          item.deberiaRacionarFinalmente
                        )}
                      </td>

                      <td
                        style={{
                          ...tdStyle,
                          whiteSpace:
                            "normal",
                          minWidth:
                            "300px",
                        }}
                      >
                        {
                          item.motivoResultado
                        }
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
const InformeRacionamiento = () => {
  const [
    fecha,
    setFecha,
  ] = useState(
    obtenerFechaHoyArgentina()
  );

  const [
    tipoRacionamiento,
    setTipoRacionamiento,
  ] = useState("desayuno");

  const [
    datos,
    setDatos,
  ] = useState(null);

  const [
    datosDiaCompleto,
    setDatosDiaCompleto,
  ] = useState(null);

  const [
    cargando,
    setCargando,
  ] = useState(false);

  const [
    cargandoDia,
    setCargandoDia,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  // ============================================================
  // CONSULTA INDIVIDUAL
  // ============================================================
  const consultarRacionamiento =
    async () => {
      try {
        setCargando(true);
        setError("");
        setDatos(null);

        const body = {
          tipo_racionamiento:
            tipoRacionamiento,
          fecha,
        };

        console.log(
          "======================================"
        );

        console.log(
          "🍽️ CONSULTANDO RACIONAMIENTO"
        );

        console.log(
          "BODY:",
          body
        );

        const respuesta =
          await axios.post(
            API_URL,
            body,
            {
              headers: {
                "Content-Type":
                  "application/json",
              },
            }
          );

        console.log(
          "✅ RESPUESTA:",
          respuesta.data
        );

        setDatos(respuesta.data);
      } catch (err) {
        console.error(err);

        setError(
          err?.response?.data
            ?.message ||
            err?.response?.data
              ?.mensaje ||
            "No se pudo consultar."
        );
      } finally {
        setCargando(false);
      }
    };

  // ============================================================
  // CONSULTAR UN TIPO
  // ============================================================
  const consultarTipoRacionamiento =
    async (tipo) => {
      const body = {
        tipo_racionamiento: tipo,
        fecha,
      };

      console.log(
        `🍽️ Consultando ${tipo}`,
        body
      );

      const respuesta =
        await axios.post(
          API_URL,
          body,
          {
            headers: {
              "Content-Type":
                "application/json",
            },
          }
        );

      return respuesta.data;
    };

  // ============================================================
  // CONSULTAR DÍA COMPLETO
  // ============================================================
  const consultarDiaCompleto =
    async () => {
      try {
        if (!fecha) {
          alert(
            "Debe seleccionar una fecha."
          );

          return null;
        }

        setCargandoDia(true);
        setError("");

        const tipos = [
          "desayuno",
          "almuerzo",
          "merienda",
          "cena",
        ];

        const respuestas =
          await Promise.all(
            tipos.map((tipo) =>
              consultarTipoRacionamiento(
                tipo
              )
            )
          );

        const resultado = {
          fecha,
          desayuno: respuestas[0],
          almuerzo: respuestas[1],
          merienda: respuestas[2],
          cena: respuestas[3],
        };

        console.log(
          "📅 DÍA COMPLETO:",
          resultado
        );

        setDatosDiaCompleto(
          resultado
        );

        return resultado;
      } catch (err) {
        console.error(
          "❌ ERROR DÍA COMPLETO",
          err
        );

        setError(
          err?.response?.data
            ?.message ||
            "No se pudo consultar el día completo."
        );

        return null;
      } finally {
        setCargandoDia(false);
      }
    };

  // ============================================================
  // PREPARAR LISTADO
  // ============================================================
  const prepararListadoRacionamiento = (
    nombreSede,
    sede
  ) => {
    if (!sede) return [];

    const primer =
      sede
        ?.deberianRacionarListado
        ?.primerAnio || [];

    const segundo =
      sede
        ?.deberianRacionarListado
        ?.segundoAnio || [];

    const primero = primer.map(
      (cadete, index) => ({
        Numero: index + 1,
        Sede: nombreSede,
        Anio: "1° Año",
        DNI: cadete.dni,
        Apellido: (
          cadete.apellido || ""
        ).trim(),
        Nombre: (
          cadete.nombre || ""
        ).trim(),
      })
    );

    const segundos = segundo.map(
      (cadete, index) => ({
        Numero:
          primero.length +
          index +
          1,

        Sede: nombreSede,
        Anio: "2° Año",
        DNI: cadete.dni,
        Apellido: (
          cadete.apellido || ""
        ).trim(),
        Nombre: (
          cadete.nombre || ""
        ).trim(),
      })
    );

    return [
      ...primero,
      ...segundos,
    ];
  };

  // ============================================================
  // PREPARAR PASES
  // ============================================================
  const prepararPases = (
    nombreSede,
    sede
  ) => {
    if (!sede?.pasesDetalle) {
      return [];
    }

    return sede.pasesDetalle.map(
      (item, index) => ({
        Numero: index + 1,

        Sede:
          item.sede ||
          nombreSede,

        DNI: item.dni,

        Apellido: (
          item.apellido || ""
        ).trim(),

        Nombre: (
          item.nombre || ""
        ).trim(),

        Anio: item.anio || "",

        Seccion:
          item.seccionDescripcion ||
          "",

        Asistencia: siNo(
          item.tieneAsistenciaEnRacionamiento
        ),

        Ausencia: siNo(
          item.tieneAusencia
        ),

        Pase: siNo(
          item.tienePaseSalida
        ),

        "Pase excluye racionamiento":
          siNo(
            item.paseQueRealmenteExcluyeRacionamiento
          ),

        "Debe racionar":
          siNo(
            item.deberiaRacionarFinalmente
          ),

        Motivo:
          item.motivoResultado ||
          "",
      })
    );
  };

  // ============================================================
  // RESUMEN GENERAL
  // ============================================================
  const prepararResumenGeneral = (
    respuesta
  ) => {
    const t =
      respuesta?.totales || {};

    return [
      {
        Concepto: "Fecha",
        Valor:
          respuesta?.fecha ||
          fecha,
      },

      {
        Concepto:
          "Racionamiento",
        Valor:
          respuesta?.tipoRacionamiento ||
          tipoRacionamiento,
      },

      {
        Concepto:
          "Hora inicio",
        Valor:
          respuesta
            ?.horaInicioRacionamiento ||
          "",
      },

      {
        Concepto: "Hora fin",
        Valor:
          respuesta
            ?.horaFinRacionamiento ||
          "",
      },

      {
        Concepto:
          "Total cadetes",
        Valor:
          t.totalCadetes ?? 0,
      },

      {
        Concepto:
          "Presentes",
        Valor:
          t.totalPresentes ?? 0,
      },

      {
        Concepto:
          "Ausencias",
        Valor:
          t.totalAusencias ?? 0,
      },

      {
        Concepto:
          "Con pase de salida",
        Valor:
          t.totalConPaseSalida ?? 0,
      },

      {
        Concepto:
          "Pases con asistencia",
        Valor:
          t.pasesConAsistencia ??
          0,
      },

      {
        Concepto:
          "Pases sin asistencia",
        Valor:
          t.pasesSinAsistencia ??
          0,
      },

      {
        Concepto:
          "DEBEN RACIONAR",
        Valor:
          t.deberianRacionar ?? 0,
      },
    ];
  };

  // ============================================================
  // AGREGAR HOJA
  // ============================================================
  const agregarHoja = (
    workbook,
    nombre,
    data
  ) => {
    const filas =
      data?.length > 0
        ? data
        : [
            {
              Informacion:
                "Sin datos",
            },
          ];

    const worksheet =
      XLSX.utils.json_to_sheet(
        filas
      );

    const keys =
      Object.keys(filas[0]);

    worksheet["!cols"] =
      keys.map((key) => {
        let max =
          key.length;

        filas.forEach((fila) => {
          const valor =
            String(
              fila[key] ?? ""
            );

          max = Math.max(
            max,
            valor.length
          );
        });

        return {
          wch: Math.min(
            max + 2,
            45
          ),
        };
      });

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      nombre.substring(0, 31)
    );
  };

  // ============================================================
  // EXCEL INDIVIDUAL
  // ============================================================
  const descargarExcel = () => {
    if (!datos) {
      alert(
        "Primero debe realizar una consulta."
      );

      return;
    }

    const workbook =
      XLSX.utils.book_new();

    agregarHoja(
      workbook,
      "Resumen",
      prepararResumenGeneral(
        datos
      )
    );

    agregarHoja(
      workbook,
      "Racionan Recreo",
      prepararListadoRacionamiento(
        "Recreo",
        datos.recreo
      )
    );

    agregarHoja(
      workbook,
      "Racionan Rosario",
      prepararListadoRacionamiento(
        "Rosario",
        datos.rosario
      )
    );

    agregarHoja(
      workbook,
      "Pases Recreo",
      prepararPases(
        "Recreo",
        datos.recreo
      )
    );

    agregarHoja(
      workbook,
      "Pases Rosario",
      prepararPases(
        "Rosario",
        datos.rosario
      )
    );

    XLSX.writeFile(
      workbook,
      `Racionamiento_${tipoRacionamiento}_${fecha}.xlsx`
    );
  };

  // ============================================================
  // PDF INDIVIDUAL
  // ============================================================
  const descargarPdf = () => {
    if (!datos) {
      alert(
        "Primero debe realizar una consulta."
      );

      return;
    }

    const doc =
      new jsPDF({
        orientation: "landscape",
      });

    doc.setFontSize(18);

    doc.text(
      "Informe de Racionamiento",
      14,
      15
    );

    doc.setFontSize(11);

    doc.text(
      `Fecha: ${fecha}`,
      14,
      23
    );

    doc.text(
      `Tipo: ${capitalizar(
        tipoRacionamiento
      )}`,
      14,
      29
    );

    doc.text(
      `Horario: ${
        datos.horaInicioRacionamiento ||
        ""
      } a ${
        datos.horaFinRacionamiento ||
        ""
      }`,
      14,
      35
    );

    autoTable(doc, {
      startY: 42,

      head: [
        [
          "Concepto",
          "Cantidad",
        ],
      ],

      body:
        prepararResumenGeneral(
          datos
        ).map((item) => [
          item.Concepto,
          item.Valor,
        ]),

      styles: {
        fontSize: 9,
      },
    });

    let y =
      doc.lastAutoTable
        ?.finalY + 10 || 60;

    const recreo =
      prepararListadoRacionamiento(
        "Recreo",
        datos.recreo
      );

    if (recreo.length > 0) {
      doc.text(
        "Cadetes que deben racionar - Recreo",
        14,
        y
      );

      autoTable(doc, {
        startY: y + 4,

        head: [
          [
            "#",
            "Año",
            "DNI",
            "Apellido",
            "Nombre",
          ],
        ],

        body: recreo.map(
          (item) => [
            item.Numero,
            item.Anio,
            item.DNI,
            item.Apellido,
            item.Nombre,
          ]
        ),

        styles: {
          fontSize: 8,
        },
      });

      y =
        doc.lastAutoTable
          .finalY + 10;
    }

    const rosario =
      prepararListadoRacionamiento(
        "Rosario",
        datos.rosario
      );

    if (rosario.length > 0) {
      if (y > 170) {
        doc.addPage();
        y = 20;
      }

      doc.text(
        "Cadetes que deben racionar - Rosario",
        14,
        y
      );

      autoTable(doc, {
        startY: y + 4,

        head: [
          [
            "#",
            "Año",
            "DNI",
            "Apellido",
            "Nombre",
          ],
        ],

        body: rosario.map(
          (item) => [
            item.Numero,
            item.Anio,
            item.DNI,
            item.Apellido,
            item.Nombre,
          ]
        ),

        styles: {
          fontSize: 8,
        },
      });
    }

    doc.save(
      `Racionamiento_${tipoRacionamiento}_${fecha}.pdf`
    );
  };

  // ============================================================
  // CONSOLIDADO DEL DÍA
  // ============================================================
  const generarConsolidadoDiario = (
    resultadoDia
  ) => {
    const mapa =
      new Map();

    const tipos = [
      "desayuno",
      "almuerzo",
      "merienda",
      "cena",
    ];

    tipos.forEach((tipo) => {
      const respuesta =
        resultadoDia?.[tipo];

      if (!respuesta) return;

      [
        ["Recreo", respuesta.recreo],
        [
          "Rosario",
          respuesta.rosario,
        ],
      ].forEach(
        ([nombreSede, sede]) => {
          if (!sede) return;

          const procesar = (
            listado,
            anio
          ) => {
            listado.forEach(
              (cadete) => {
                const dni = String(
                  cadete.dni || ""
                );

                if (!dni) return;

                if (
                  !mapa.has(dni)
                ) {
                  mapa.set(dni, {
                    DNI: dni,

                    Apellido: (
                      cadete.apellido ||
                      ""
                    ).trim(),

                    Nombre: (
                      cadete.nombre ||
                      ""
                    ).trim(),

                    Sede:
                      nombreSede,

                    Anio: anio,

                    Desayuno:
                      "NO",

                    Almuerzo:
                      "NO",

                    Merienda:
                      "NO",

                    Cena: "NO",

                    TotalRaciones: 0,

                    Detalle: "",
                  });
                }

                const registro =
                  mapa.get(dni);

                const campo =
                  capitalizar(tipo);

                registro[campo] =
                  "SI";
              }
            );
          };

          procesar(
            sede
              ?.deberianRacionarListado
              ?.primerAnio ||
              [],
            "1° Año"
          );

          procesar(
            sede
              ?.deberianRacionarListado
              ?.segundoAnio ||
              [],
            "2° Año"
          );
        }
      );
    });

    mapa.forEach(
      (registro) => {
        let total = 0;

        const comidas = [];

        if (
          registro.Desayuno ===
          "SI"
        ) {
          total++;
          comidas.push(
            "Desayuno"
          );
        }

        if (
          registro.Almuerzo ===
          "SI"
        ) {
          total++;
          comidas.push(
            "Almuerzo"
          );
        }

        if (
          registro.Merienda ===
          "SI"
        ) {
          total++;
          comidas.push(
            "Merienda"
          );
        }

        if (
          registro.Cena === "SI"
        ) {
          total++;
          comidas.push("Cena");
        }

        registro.TotalRaciones =
          total;

        registro.Detalle =
          comidas.join(", ");
      }
    );

    return Array.from(
      mapa.values()
    ).sort((a, b) => {
      if (
        a.Sede !== b.Sede
      ) {
        return a.Sede.localeCompare(
          b.Sede
        );
      }

      if (
        a.Anio !== b.Anio
      ) {
        return a.Anio.localeCompare(
          b.Anio
        );
      }

      return a.Apellido.localeCompare(
        b.Apellido
      );
    });
  };

  // ============================================================
  // RESUMEN DÍA
  // ============================================================
  const generarResumenDia = (
    resultadoDia
  ) => {
    const tipos = [
      "desayuno",
      "almuerzo",
      "merienda",
      "cena",
    ];

    return tipos.map(
      (tipo) => {
        const respuesta =
          resultadoDia?.[tipo];

        return {
          Fecha: fecha,

          Racionamiento:
            capitalizar(tipo),

          Horario: `${
            respuesta
              ?.horaInicioRacionamiento ||
            ""
          } - ${
            respuesta
              ?.horaFinRacionamiento ||
            ""
          }`,

          "Total general":
            respuesta?.totales
              ?.deberianRacionar ??
            0,

          Recreo:
            respuesta?.recreo
              ?.deberianRacionar ??
            0,

          Rosario:
            respuesta?.rosario
              ?.deberianRacionar ??
            0,

          "Primer Año":
            (respuesta?.recreo
              ?.porAnio
              ?.primerAnio
              ?.deberianRacionar ??
              0) +
            (respuesta?.rosario
              ?.porAnio
              ?.primerAnio
              ?.deberianRacionar ??
              0),

          "Segundo Año":
            (respuesta?.recreo
              ?.porAnio
              ?.segundoAnio
              ?.deberianRacionar ??
              0) +
            (respuesta?.rosario
              ?.porAnio
              ?.segundoAnio
              ?.deberianRacionar ??
              0),
        };
      }
    );
  };

  // ============================================================
  // EXCEL DÍA COMPLETO
  // ============================================================
  const descargarExcelDiaCompleto =
    async () => {
      let resultado =
        datosDiaCompleto;

      if (!resultado) {
        resultado =
          await consultarDiaCompleto();
      }

      if (!resultado) return;

      const workbook =
        XLSX.utils.book_new();

      const resumen =
        generarResumenDia(
          resultado
        );

      const consolidado =
        generarConsolidadoDiario(
          resultado
        );

      agregarHoja(
        workbook,
        "Resumen Dia",
        resumen
      );

      agregarHoja(
        workbook,
        "Consolidado Cadetes",
        consolidado
      );

      [
        "desayuno",
        "almuerzo",
        "merienda",
        "cena",
      ].forEach((tipo) => {
        const respuesta =
          resultado[tipo];

        const listado = [
          ...prepararListadoRacionamiento(
            "Recreo",
            respuesta?.recreo
          ),

          ...prepararListadoRacionamiento(
            "Rosario",
            respuesta?.rosario
          ),
        ];

        agregarHoja(
          workbook,
          capitalizar(tipo),
          listado
        );
      });

      const pasesDia = [];

      [
        "desayuno",
        "almuerzo",
        "merienda",
        "cena",
      ].forEach((tipo) => {
        const respuesta =
          resultado[tipo];

        const recreo =
          prepararPases(
            "Recreo",
            respuesta?.recreo
          ).map((item) => ({
            Racionamiento:
              capitalizar(tipo),
            ...item,
          }));

        const rosario =
          prepararPases(
            "Rosario",
            respuesta?.rosario
          ).map((item) => ({
            Racionamiento:
              capitalizar(tipo),
            ...item,
          }));

        pasesDia.push(
          ...recreo,
          ...rosario
        );
      });

      agregarHoja(
        workbook,
        "Pases Dia",
        pasesDia
      );

      XLSX.writeFile(
        workbook,
        `Racionamiento_Dia_Completo_${fecha}.xlsx`
      );
    };

  // ============================================================
  // PDF DÍA COMPLETO
  // ============================================================
  const descargarPdfDiaCompleto =
    async () => {
      let resultado =
        datosDiaCompleto;

      if (!resultado) {
        resultado =
          await consultarDiaCompleto();
      }

      if (!resultado) return;

      const resumen =
        generarResumenDia(
          resultado
        );

      const consolidado =
        generarConsolidadoDiario(
          resultado
        );

      const doc =
        new jsPDF({
          orientation:
            "landscape",
          unit: "mm",
          format: "a4",
        });

      doc.setFontSize(18);

      doc.text(
        "Informe Diario de Racionamiento",
        14,
        15
      );

      doc.setFontSize(11);

      doc.text(
        `Fecha: ${fecha}`,
        14,
        23
      );

      // ========================================================
      // RESUMEN
      // ========================================================
      autoTable(doc, {
        startY: 30,

        head: [
          [
            "Racionamiento",
            "Horario",
            "General",
            "Recreo",
            "Rosario",
            "1° Año",
            "2° Año",
          ],
        ],

        body: resumen.map(
          (item) => [
            item.Racionamiento,
            item.Horario,
            item[
              "Total general"
            ],
            item.Recreo,
            item.Rosario,
            item[
              "Primer Año"
            ],
            item[
              "Segundo Año"
            ],
          ]
        ),

        styles: {
          fontSize: 9,
        },
      });

      let y =
        doc.lastAutoTable
          .finalY + 10;

      doc.setFontSize(13);

      doc.text(
        "Consolidado por cadete",
        14,
        y
      );

      autoTable(doc, {
        startY: y + 4,

        head: [
          [
            "DNI",
            "Apellido",
            "Nombre",
            "Sede",
            "Año",
            "Des",
            "Alm",
            "Mer",
            "Cena",
            "Total",
            "Detalle",
          ],
        ],

        body:
          consolidado.map(
            (item) => [
              item.DNI,
              item.Apellido,
              item.Nombre,
              item.Sede,
              item.Anio,
              item.Desayuno,
              item.Almuerzo,
              item.Merienda,
              item.Cena,
              item.TotalRaciones,
              item.Detalle,
            ]
          ),

        styles: {
          fontSize: 7,
        },

        columnStyles: {
          10: {
            cellWidth: 38,
          },
        },
      });

      // ========================================================
      // UNA SECCIÓN POR COMIDA
      // ========================================================
      [
        "desayuno",
        "almuerzo",
        "merienda",
        "cena",
      ].forEach((tipo) => {
        const respuesta =
          resultado[tipo];

        const listado = [
          ...prepararListadoRacionamiento(
            "Recreo",
            respuesta?.recreo
          ),
          ...prepararListadoRacionamiento(
            "Rosario",
            respuesta?.rosario
          ),
        ];

        doc.addPage();

        doc.setFontSize(16);

        doc.text(
          capitalizar(tipo),
          14,
          15
        );

        doc.setFontSize(10);

        doc.text(
          `Total que deben racionar: ${
            respuesta?.totales
              ?.deberianRacionar ??
            0
          }`,
          14,
          22
        );

        autoTable(doc, {
          startY: 28,

          head: [
            [
              "#",
              "Sede",
              "Año",
              "DNI",
              "Apellido",
              "Nombre",
            ],
          ],

          body: listado.map(
            (item) => [
              item.Numero,
              item.Sede,
              item.Anio,
              item.DNI,
              item.Apellido,
              item.Nombre,
            ]
          ),

          styles: {
            fontSize: 8,
          },
        });
      });

      doc.save(
        `Racionamiento_Dia_Completo_${fecha}.pdf`
      );
    };

  // ============================================================
  // TOTAL RACIONES DEL DÍA
  // ============================================================
  const obtenerTotalRacionesDia =
    () => {
      if (!datosDiaCompleto) {
        return 0;
      }

      return [
        "desayuno",
        "almuerzo",
        "merienda",
        "cena",
      ].reduce(
        (acum, tipo) =>
          acum +
          (datosDiaCompleto?.[
            tipo
          ]?.totales
            ?.deberianRacionar ??
            0),
        0
      );
    };

  return (
    <div
      style={{
        maxWidth: "1500px",
        margin: "0 auto",
        padding: "20px",
        fontFamily:
          "Arial, Helvetica, sans-serif",
      }}
    >
      <div
        style={{
          marginBottom: "25px",
        }}
      >
        <h1
          style={{
            marginBottom: "5px",
          }}
        >
          🍽️ Racionamiento de Cadetes
        </h1>

        <p
          style={{
            color: "#6c757d",
          }}
        >
          Control de desayuno,
          almuerzo, merienda y cena.
        </p>
      </div>

      {/* ======================================================
          FILTROS
      ====================================================== */}

      <div
        style={{
          display: "flex",
          gap: "15px",
          flexWrap: "wrap",
          alignItems: "end",
          padding: "20px",
          background: "#f8f9fa",
          borderRadius: "12px",
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: "220px",
          }}
        >
          <label style={labelStyle}>
            Fecha
          </label>

          <input
            type="date"
            value={fecha}
            onChange={(e) => {
              setFecha(
                e.target.value
              );

              setDatosDiaCompleto(
                null
              );
            }}
            style={inputStyle}
          />
        </div>

        <div
          style={{
            flex: 1,
            minWidth: "220px",
          }}
        >
          <label style={labelStyle}>
            Tipo
          </label>

          <select
            value={
              tipoRacionamiento
            }
            onChange={(e) =>
              setTipoRacionamiento(
                e.target.value
              )
            }
            style={inputStyle}
          >
            <option value="desayuno">
              Desayuno
            </option>

            <option value="almuerzo">
              Almuerzo
            </option>

            <option value="merienda">
              Merienda
            </option>

            <option value="cena">
              Cena
            </option>
          </select>
        </div>

        <button
          onClick={
            consultarRacionamiento
          }
          disabled={cargando}
          style={buttonPrimary}
        >
          {cargando
            ? "Consultando..."
            : "🔎 Consultar"}
        </button>

        {datos && (
          <>
            <button
              onClick={
                descargarExcel
              }
              style={buttonExcel}
            >
              📊 Excel
            </button>

            <button
              onClick={
                descargarPdf
              }
              style={buttonPdf}
            >
              📄 PDF
            </button>
          </>
        )}
      </div>

      {/* ======================================================
          DÍA COMPLETO
      ====================================================== */}

      <div
        style={{
          marginTop: "20px",
          padding: "20px",
          background: "#f3f0ff",
          border:
            "1px solid #b197fc",
          borderRadius: "12px",
        }}
      >
        <h3
          style={{
            marginTop: 0,
          }}
        >
          📅 Informe del día completo
        </h3>

        <p
          style={{
            color: "#5f3dc4",
          }}
        >
          Consulta automáticamente
          desayuno, almuerzo,
          merienda y cena.
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <button
            onClick={
              consultarDiaCompleto
            }
            disabled={
              cargandoDia
            }
            style={
              buttonDiaCompleto
            }
          >
            {cargandoDia
              ? "Consultando las 4 raciones..."
              : "📅 Analizar día completo"}
          </button>

          <button
            onClick={
              descargarExcelDiaCompleto
            }
            disabled={
              cargandoDia
            }
            style={buttonExcel}
          >
            📊 Excel día completo
          </button>

          <button
            onClick={
              descargarPdfDiaCompleto
            }
            disabled={
              cargandoDia
            }
            style={buttonPdf}
          >
            📄 PDF día completo
          </button>
        </div>
      </div>

      {/* ======================================================
          ERROR
      ====================================================== */}

      {error && (
        <div
          style={{
            marginTop: "20px",
            padding: "15px",
            background: "#fff5f5",
            color: "#c92a2a",
            border:
              "1px solid #ffa8a8",
            borderRadius: "10px",
          }}
        >
          {error}
        </div>
      )}

      {/* ======================================================
          RESULTADO DÍA COMPLETO
      ====================================================== */}

      {datosDiaCompleto && (
        <div
          style={{
            marginTop: "30px",
          }}
        >
          <h2>
            📅 Resumen del día
          </h2>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            {[
              "desayuno",
              "almuerzo",
              "merienda",
              "cena",
            ].map((tipo) => (
              <Tarjeta
                key={tipo}
                titulo={capitalizar(
                  tipo
                )}
                valor={
                  datosDiaCompleto?.[
                    tipo
                  ]?.totales
                    ?.deberianRacionar ??
                  0
                }
                destacado
              />
            ))}

            <Tarjeta
              titulo="Total de raciones del día"
              valor={
                obtenerTotalRacionesDia()
              }
              destacado
            />
          </div>

          <div
            style={{
              marginTop: "25px",
              overflowX: "auto",
            }}
          >
            <h3>
              Consolidado por cadete
            </h3>

            <table
              style={{
                width: "100%",
                borderCollapse:
                  "collapse",
              }}
            >
              <thead>
                <tr
                  style={{
                    background:
                      "#212529",
                    color: "white",
                  }}
                >
                  <th style={thStyle}>
                    DNI
                  </th>

                  <th style={thStyle}>
                    Apellido
                  </th>

                  <th style={thStyle}>
                    Nombre
                  </th>

                  <th style={thStyle}>
                    Sede
                  </th>

                  <th style={thStyle}>
                    Año
                  </th>

                  <th style={thStyle}>
                    Desayuno
                  </th>

                  <th style={thStyle}>
                    Almuerzo
                  </th>

                  <th style={thStyle}>
                    Merienda
                  </th>

                  <th style={thStyle}>
                    Cena
                  </th>

                  <th style={thStyle}>
                    Total
                  </th>
                </tr>
              </thead>

              <tbody>
                {generarConsolidadoDiario(
                  datosDiaCompleto
                ).map(
                  (
                    item,
                    index
                  ) => (
                    <tr
                      key={`${item.DNI}-${index}`}
                    >
                      <td style={tdStyle}>
                        {item.DNI}
                      </td>

                      <td style={tdStyle}>
                        {
                          item.Apellido
                        }
                      </td>

                      <td style={tdStyle}>
                        {item.Nombre}
                      </td>

                      <td style={tdStyle}>
                        {item.Sede}
                      </td>

                      <td style={tdStyle}>
                        {item.Anio}
                      </td>

                      <td style={tdStyle}>
                        {
                          item.Desayuno
                        }
                      </td>

                      <td style={tdStyle}>
                        {
                          item.Almuerzo
                        }
                      </td>

                      <td style={tdStyle}>
                        {
                          item.Merienda
                        }
                      </td>

                      <td style={tdStyle}>
                        {item.Cena}
                      </td>

                      <td
                        style={{
                          ...tdStyle,
                          fontWeight:
                            "bold",
                        }}
                      >
                        {
                          item.TotalRaciones
                        }
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================================================
          RESULTADO INDIVIDUAL
      ====================================================== */}

      {datos && (
        <>
          <div
            style={{
              marginTop: "30px",
              padding: "20px",
              background: "#212529",
              color: "white",
              borderRadius: "12px",
            }}
          >
            <h2
              style={{
                margin: 0,
                textTransform:
                  "capitalize",
              }}
            >
              {tipoRacionamiento}
            </h2>

            <div
              style={{
                marginTop: "8px",
              }}
            >
              Fecha: {datos.fecha}
              {" — "}
              Horario:{" "}
              {
                datos.horaInicioRacionamiento
              }{" "}
              a{" "}
              {
                datos.horaFinRacionamiento
              }
            </div>
          </div>

          {datos.totales && (
            <div
              style={{
                marginTop: "25px",
              }}
            >
              <h2>
                Resumen general
              </h2>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <Tarjeta
                  titulo="Total cadetes"
                  valor={
                    datos.totales
                      .totalCadetes
                  }
                />

                <Tarjeta
                  titulo="Presentes"
                  valor={
                    datos.totales
                      .totalPresentes
                  }
                />

                <Tarjeta
                  titulo="Ausencias"
                  valor={
                    datos.totales
                      .totalAusencias
                  }
                  peligro={
                    datos.totales
                      .totalAusencias >
                    0
                  }
                />

                <Tarjeta
                  titulo="Con pase"
                  valor={
                    datos.totales
                      .totalConPaseSalida
                  }
                />

                <Tarjeta
                  titulo="Deben racionar"
                  valor={
                    datos.totales
                      .deberianRacionar
                  }
                  destacado
                />
              </div>
            </div>
          )}

          <DetalleSede
            nombre="Recreo"
            datos={datos.recreo}
          />

          <DetalleSede
            nombre="Rosario"
            datos={datos.rosario}
          />
        </>
      )}
    </div>
  );
};

// ============================================================
// ESTILOS
// ============================================================

const labelStyle = {
  display: "block",
  marginBottom: "6px",
  fontWeight: "600",
};

const inputStyle = {
  width: "100%",
  height: "44px",
  padding: "8px 12px",
  borderRadius: "8px",
  border:
    "1px solid #ced4da",
  fontSize: "15px",
  boxSizing: "border-box",
};

const buttonPrimary = {
  border: "none",
  borderRadius: "8px",
  padding: "12px 20px",
  background: "#1971c2",
  color: "white",
  fontWeight: "600",
  cursor: "pointer",
};

const buttonSecondary = {
  border:
    "1px solid #adb5bd",
  borderRadius: "8px",
  padding: "10px 18px",
  background: "white",
  cursor: "pointer",
};

const buttonExcel = {
  border: "none",
  borderRadius: "8px",
  padding: "12px 20px",
  background: "#2b8a3e",
  color: "white",
  fontWeight: "600",
  cursor: "pointer",
};

const buttonPdf = {
  border: "none",
  borderRadius: "8px",
  padding: "12px 20px",
  background: "#c92a2a",
  color: "white",
  fontWeight: "600",
  cursor: "pointer",
};

const buttonDiaCompleto = {
  border: "none",
  borderRadius: "8px",
  padding: "12px 20px",
  background: "#7048e8",
  color: "white",
  fontWeight: "600",
  cursor: "pointer",
};

const boxStyle = {
  border:
    "1px solid #dee2e6",
  borderRadius: "10px",
  padding: "18px",
  background: "#f8f9fa",
};

const thStyle = {
  padding: "10px",
  textAlign: "left",
  borderBottom:
    "1px solid #dee2e6",
  whiteSpace: "nowrap",
  fontSize: "13px",
};

const tdStyle = {
  padding: "9px 10px",
  borderBottom:
    "1px solid #eee",
  whiteSpace: "nowrap",
  fontSize: "13px",
};

const emptyStyle = {
  padding: "20px",
  background: "#f8f9fa",
  color: "#868e96",
  borderRadius: "8px",
};

export default InformeRacionamiento;